import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [timeline, setTimeline] = useState([]);
  const [claimsCount, setClaimsCount] = useState({
    criminal: 0,
    civil: 0,
    constitutional: 0
  });
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Fetch some data to make the page more dynamic
  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to load counts from legal-claims.json
        try {
          const claimsModule = await import('../data/legal-claims.json');
          const claimsData = claimsModule.default || [];
          
          // Count claims by category
          const counts = {
            criminal: claimsData.filter(claim => claim.category === 'Criminal').length,
            civil: claimsData.filter(claim => claim.category === 'Civil').length,
            constitutional: claimsData.filter(claim => claim.category === 'Constitutional').length
          };
          
          setClaimsCount(counts);
        } catch (err) {
          console.error('Error loading claims data:', err);
        }
        
        // Try to load key timeline events
        try {
          const timelineModule = await import('../data/timeline.json');
          const timelineData = timelineModule.default || [];
          
          // Get key events only
          const keyEvents = timelineData.filter(event => 
            [23, 25, 29, 33, 38].includes(event.id)
          ).slice(0, 5);
          
          setTimeline(keyEvents);
        } catch (err) {
          console.error('Error loading timeline data:', err);
        }
      } catch (err) {
        console.error('Error loading data for homepage:', err);
      }
    };
    
    loadData();
  }, []);

  // Handler for previewing PDFs
  const handlePreviewPdf = (pdfPath) => {
    setSelectedPdf(pdfPath);
    setShowPdfPreview(true);
  };

  // Close PDF preview
  const closePdfPreview = () => {
    setShowPdfPreview(false);
    setSelectedPdf(null);
  };

  // PDF Preview Modal Component
  const PdfPreviewModal = () => {
    if (!showPdfPreview || !selectedPdf) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full h-5/6 flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="font-bold text-lg">{selectedPdf.split('/').pop()}</h3>
            <button 
              onClick={closePdfPreview}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-grow p-4 overflow-auto">
            <iframe 
              src={`${selectedPdf}#toolbar=0`} 
              className="w-full h-full"
              title="PDF Preview"
            ></iframe>
          </div>
          <div className="border-t p-4 flex justify-between">
            <a 
              href={selectedPdf} 
              download
              className="bg-blue-100 text-blue-700 px-4 py-2 rounded hover:bg-blue-200"
            >
              Download
            </a>
            <Link 
              to="/exhibits" 
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              View All Exhibits
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Render PDF Preview Modal */}
      <PdfPreviewModal />
      
      {/* Hero Section with Case Title */}
      <div className="bg-gray-50 rounded-xl shadow-md p-6 mb-8">
        <div className="md:flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-blue-800 mb-2">Mayo v. Montgomery County</h1>
            <p className="text-lg text-gray-700">
              prepared for the people who are able to help me and others who are innocently charged but trapped in a system.
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <span className="inline-block bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold mr-2">
              Urgent
            </span>
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Active Case
            </span>
          </div>
        </div>
      </div>
      
      {/* Case Overview - Enhanced and Impactful */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          A Veteran's Fight for Justice
        </h2>
        <div className="mb-6 prose max-w-none">
          <p className="text-gray-800 text-lg leading-relaxed">
            Landon J. Mayo, a 100% disabled combat veteran and former cybersecurity professional with secret clearance, has become trapped in a legal nightmare following a medical emergency on April 29, 2023. After suffering a documented seizure that caused a tire blowout, Mayo was transported to a hospital by his own choice, only to be arrested upon discharge without any field sobriety test, Miranda rights, or proper medical evaluation.
          </p>
          <p className="text-gray-800 text-lg leading-relaxed">
            Over the subsequent 19 months, Mayo has endured:
          </p>
          <ul className="list-disc list-inside text-gray-800 text-lg leading-relaxed">
            <li>102 days of incarceration across six separate instances</li>
            <li>$59,000+ in escalating bond payments</li>
            <li>Complete loss of VA disability benefits despite no conviction</li>
            <li>Forfeiture of a $125,000/year cybersecurity career opportunity</li>
            <li>Homelessness and financial ruin</li>
          </ul>
          <p className="text-gray-800 text-lg leading-relaxed">
            Critical evidence that would exonerate Mayo has vanished, including police dashcam footage (confirmed lost in a system hack), the original responding officer's report, and accurate medical records. Independent hair follicle testing directly contradicted hospital blood results, yet authorities have repeatedly violated Mayo's rights by dismissing his documented medical conditions and prescription medications.
          </p>
          <p className="text-gray-800 text-lg leading-relaxed">
            This case represents not just one veteran's struggle, but a systemic failure to protect those who have sacrificed for their country, especially those with service-connected disabilities that can mimic intoxication.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
            <h3 className="font-bold text-red-800 text-lg mb-2">Critical Evidence Missing due to City-Hack</h3>
            <p className="text-gray-700">
              Dashcam footage unavailable due to confirmed city-wide system hack at Shenandoah Police Department. The breach was never disclosed, violating <a href="https://www.texasattorneygeneral.gov/consumer-protection/data-breach-reporting" target="_blank" rel="noopener noreferrer" className="underline text-blue-700">SB 271</a>.<br /><br />
              No police report filed by first responding Shenandoah officer who witnessed your condition before hospital transport.<br /><br />
              Discrepancies in hospital lab results showing substances never consumed, contradicted by independent hair follicle testing that returned negative results.<br /><br />
              Vehicle disabled due to tire blowout, waited 10-15 minutes before officer arrival.<br /><br />
              Medical transportation was voluntary with no police directive.<br /><br />
              VA Hospital documentation confirming scheduled appointment during missed court appearance.
            </p>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
            <h3 className="font-bold text-amber-800 text-lg mb-2">Loss of Home & Livelihood</h3>
            <p className="text-gray-700">
              <span className="font-semibold">Loss of VA Benefits:</span><br />
              Suspension of VA disability compensation due to being classified as a "fugitive felon"<br />
              No conviction required for VA to suspend benefits under fugitive felon status<br />
              Interruption of critical medical care for service-connected disabilities<br />
              Loss of VA housing assistance eligibility<br />
              Removal from VA treatment programs despite 100% disability rating
              <br /><br />
              <span className="font-semibold">Employment Opportunities Lost:</span><br />
              Missed job interview with Apple scheduled for April 28, 2023<br />
              Lost cybersecurity position with A-LIGN offering $125,000+ salary<br />
              Potential for advancement in penetration testing career derailed
              <br /><br />
              <span className="font-semibold">Financial Devastation:</span><br />
              Total bond payments: $59,000+ over 19 months<br />
              Additional costs for interlock device ($175+)<br />
              Mandatory drug testing fees<br />
              Court costs and legal fees exceeding $500
              <br /><br />
              <span className="font-semibold">Time Incarcerated:</span><br />
              2023: 39 days (August-September, September-October)<br />
              2024: 63+ days (February-March, October-November)<br />
              Total: 102+ days over a 19-month period
              <br /><br />
              <span className="font-semibold">Housing Instability:</span><br />
              Inability to maintain consistent employment due to incarceration<br />
              Difficulties with transportation after vehicle issues<br />
              Limited communication access complicating court and probation requirements<br />
              Missed VA appointments for necessary medical care
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
            <h3 className="font-bold text-blue-800 text-lg mb-2">Medical Condition Mischaracterized</h3>
            <p className="text-gray-700">
              Documented history of seizures and ADHD in medical records disregarded by authorities<br />
              Post-seizure symptoms and prescribed medication effects mistakenly identified as intoxication<br />
              No field sobriety test or breathalyzer administered despite my requests<br />
              VA-documented 100% disability status ignored during arrest and subsequent proceedings<br />
              Independent hair follicle testing directly contradicted hospital lab results<br />
              Hospital records show numerous inconsistencies, including "positive" test results for substances not consumed<br />
              Medical conditions exacerbated by repeated incarceration and interrupted treatment
            </p>
          </div>
        </div>
        
        {/* Key facts timeline */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Critical Case Timeline
          </h3>
          
          <div className="relative border-l-2 border-gray-200 ml-3 pl-8 space-y-6">
            {timeline.length > 0 ? (
              timeline.map((event, index) => (
                <div key={event.id} className="relative">
                  <div className="absolute -left-10 mt-1.5 h-4 w-4 rounded-full bg-blue-500"></div>
                  <div>
                    <span className="text-sm font-medium text-blue-600">{event.date}, {event.time}</span>
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-600">{event.description}</p>
                    {event.attachments && event.attachments.length > 0 && (
                      <div className="mt-1">
                        <Link to="/exhibits" className="text-xs text-blue-600 hover:underline">
                          View Evidence
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="relative">
                  <div className="absolute -left-10 mt-1.5 h-4 w-4 rounded-full bg-blue-500"></div>
                  <div>
                    <span className="text-sm font-medium text-blue-600">April 29, 2023, 2:20 AM</span>
                    <h4 className="font-medium text-gray-900">Vehicle Accident</h4>
                    <p className="text-sm text-gray-600">Suffered seizure while driving, resulting in accident. Airbags deployed causing facial irritation and injuries.</p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-10 mt-1.5 h-4 w-4 rounded-full bg-blue-500"></div>
                  <div>
                    <span className="text-sm font-medium text-blue-600">April 29, 2023, 10:00 AM</span>
                    <h4 className="font-medium text-gray-900">Unlawful Arrest</h4>
                    <p className="text-sm text-gray-600">Arrested for DWI after hospital discharge with no Miranda rights, no field sobriety test, and no proper search.</p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-10 mt-1.5 h-4 w-4 rounded-full bg-red-500"></div>
                  <div>
                    <span className="text-sm font-medium text-red-600">May 3, 2024</span>
                    <h4 className="font-medium text-gray-900">Confirmation of Missing Evidence</h4>
                    <p className="text-sm text-gray-600">Shenandoah PD confirmed city-wide system hack resulted in loss of police dashcam footage and no police report was filed.</p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-10 mt-1.5 h-4 w-4 rounded-full bg-red-500"></div>
                  <div>
                    <span className="text-sm font-medium text-red-600">February 12, 2024</span>
                    <h4 className="font-medium text-gray-900">VA Benefits Terminated</h4>
                    <p className="text-sm text-gray-600">Benefits terminated due to false classification as "fugitive felon" based on false arrest claims.</p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-4 text-right">
            <Link to="/timeline" className="inline-flex items-center text-blue-600 font-medium hover:underline">
              View Full Timeline
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Legal Claims Summary */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
          Legal Claims Summary
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 p-5 rounded-lg border border-red-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-red-800">Criminal Claims</h3>
              <span className="bg-red-200 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                {claimsCount.criminal || 3}
              </span>
            </div>
            <ul className="text-sm space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1.5 mr-2"></span>
                Unlawful arrest without probable cause
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1.5 mr-2"></span>
                Procedural violations & chain of custody breach
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1.5 mr-2"></span>
                Bond officer misconduct & false reporting
              </li>
            </ul>
            <div className="mt-4">
              <Link to="/legal-claims" className="text-red-700 text-sm font-medium hover:underline">
                View Criminal Claims →
              </Link>
            </div>
          </div>
          
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-blue-800">Civil Claims</h3>
              <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                {claimsCount.civil || 2}
              </span>
            </div>
            <ul className="text-sm space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
                Medical negligence - failure to identify seizure
              </li>
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
                Veteran benefits unlawfully terminated
              </li>
              <li className="flex items-start opacity-50">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
                Financial damages & housing instability
              </li>
            </ul>
            <div className="mt-4">
              <Link to="/legal-claims" className="text-blue-700 text-sm font-medium hover:underline">
                View Civil Claims →
              </Link>
            </div>
          </div>
          
          <div className="bg-purple-50 p-5 rounded-lg border border-purple-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-purple-800">Constitutional Claims</h3>
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded-full text-sm font-medium">
                {claimsCount.constitutional || 1}
              </span>
            </div>
            <ul className="text-sm space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
                Evidence tampering & Brady violation
              </li>
              <li className="flex items-start opacity-50">
                <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
                Due process rights violations
              </li>
              <li className="flex items-start opacity-50">
                <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
                Fourth Amendment search & seizure violations
              </li>
            </ul>
            <div className="mt-4">
              <Link to="/legal-claims" className="text-purple-700 text-sm font-medium hover:underline">
                View Constitutional Claims →
              </Link>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-5 rounded-lg">
          <h3 className="font-bold text-gray-900 mb-3">Critical Evidence Highlights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex">
              <div className="flex-shrink-0 bg-green-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 01-1.122-5.686" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="font-medium text-gray-900">Police Call Recording</h4>
                <p className="text-sm text-gray-600">Shenandoah PD confirms system hack resulting in lost dashcam footage.</p>
                <button onClick={() => handlePreviewPdf('/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf')} className="text-xs text-blue-600 hover:underline">Preview Recording Transcript</button>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="font-medium text-gray-900">Hospital Records</h4>
                <p className="text-sm text-gray-600">Hospital confirms firearm magazine left behind, proving no search occurred.</p>
                <button onClick={() => handlePreviewPdf('/evidence/documents/20241220_EXHIBIT-C3_Herman_Memorial_Hospital_Gun_Clip_Left_at_ER.pdf')} className="text-xs text-blue-600 hover:underline">Preview Document</button>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="font-medium text-gray-900">Hair Follicle Test</h4>
                <p className="text-sm text-gray-600">Independent test shows no substances present for 90 days prior to incident.</p>
                {/* <button onClick={() => handlePreviewPdf('/evidence/exhibits/Test-Sobriety-Hair-Follicle.pdf')} className="text-xs text-blue-600 hover:underline">Preview Document</button> */}
                <button onClick={() => handlePreviewPdf('/evidence/exhibits/20230630_EXHIBIT-D1_Hair-Follicle-Test-Results.pdf')} className="text-xs text-blue-600 hover:underline">Preview Document</button>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 bg-red-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="font-medium text-gray-900">VA Benefits Termination</h4>
                <p className="text-sm text-gray-600">Official letter from VA terminating benefits due to false fugitive felon classification.</p>
                <button onClick={() => handlePreviewPdf('/evidence/exhibits/20240212_EXHIBIT-D12_Termination-of-VA-Benefits.pdf')} className="text-xs text-blue-600 hover:underline">Preview Document</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation Guide */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Case Navigation
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/timeline" className="group">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow h-full">
              <div className="flex items-center mb-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="ml-3 font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                  Timeline
                </h3>
              </div>
              <p className="text-sm text-gray-600">Interactive chronology of key events with supporting evidence.</p>
            </div>
          </Link>
          
          <Link to="/legal-claims" className="group">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow h-full">
              <div className="flex items-center mb-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="ml-3 font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                  Legal Claims
                </h3>
              </div>
              <p className="text-sm text-gray-600">Detailed breakdown of each legal claim with supporting evidence.</p>
            </div>
          </Link>
          
          <Link to="/exhibits" className="group">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow h-full">
              <div className="flex items-center mb-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="ml-3 font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                  Exhibits
                </h3>
              </div>
              <p className="text-sm text-gray-600">Searchable database of all case exhibits with filtering options.</p>
            </div>
          </Link>
        </div>
      </div>
      
      {/* Contact Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Why This Site Exists</h2>
            <p className="text-gray-700 mb-4">
              This site was created to shine a light on how easily justice can be derailed when key evidence goes missing and medical conditions are misunderstood. My story reveals how a single car accident with a tire blowout spiraled into nearly two years of legal battles, over 100 days of incarceration, and the loss of my career, home, and VA benefits—all without a conviction.
            </p>
            <p className="text-gray-700 mb-4">
              Through documenting this journey, I hope to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4">
              <li>Show how quickly someone's life can be upended by a flawed system</li>
              <li>Highlight the particular vulnerability of veterans with service-connected disabilities</li>
              <li>Demonstrate how critical evidence can disappear, leaving the accused without means to defend themselves</li>
              <li>Inspire reform in how medical conditions are considered in legal proceedings</li>
              <li>Provide hope and guidance to others facing similar situations</li>
            </ul>
            <p className="text-gray-700">
              The road to justice isn't always straight, but by sharing my experience, perhaps others won't have to travel it alone. This site stands as both a testament to perseverance and a call for change in how we treat those caught in the criminal justice system—especially those who have served their country.
            </p>
          </div>
          <div className="bg-gray-50 p-5 rounded-lg">
            <h3 className="font-bold text-gray-900 mb-3">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">Landon J. Mayo</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">(832) 510-4535</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">landonmayo722@gmail.com</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">Disabled Veteran</p>
                </div>
              </div>
              
              <div className="pt-4 mt-4 border-t border-gray-200">
                <a href="mailto:landonmayo722@gmail.com" className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded text-center transition-colors">
                  Contact Directly
                </a>
                <p className="text-xs text-center text-gray-500 mt-2">All evidence and documentation available upon request</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer with Evidence Reference */}
      <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center">
        <p className="text-gray-600 text-sm">
          Evidence documented under case reference: <span className="font-medium">MCDC-2023-49285-B</span>
        </p>
        <p className="text-gray-500 text-xs mt-1">
          All evidence timestamped and preserved with digital chain of custody
        </p>
      </div>
    </div>
  );
};

export default HomePage;